import { useTheme } from '@shopify/restyle';
import type { ButtonProps } from 'app/design/primitives/button';
import { Button, PressableType } from 'app/design/primitives/button';
import { isMobileNative, useMaxWidth, useUISize } from 'app/utils/web-helpers';
import type { ReactElement, ReactNode } from 'react';
import { Children, cloneElement, isValidElement, useEffect, useRef } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import type { ActionSheetProps, ActionSheetRef } from 'react-native-actions-sheet';
import ActionSheet from 'react-native-actions-sheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Text } from './text';
import type { Theme } from 'app/shopify';

export interface BottomSheetProps extends ActionSheetProps {
  /** Styles the wrapper around sheet children (useful for full-height embedded screens). */
  contentStyle?: StyleProp<ViewStyle>;
  open?: boolean;
  showIndicator?: boolean;
  forceZeroPadding?: boolean;
}

export function BottomSheet({
  containerStyle,
  contentStyle,
  indicatorStyle,
  open,
  showIndicator = true,
  useBottomSafeAreaPadding = true,
  forceZeroPadding = false,
  ...rest
}: BottomSheetProps) {
  const theme = useTheme<Theme>();
  const sheet = useRef<ActionSheetRef>(null);
  const safeAreaInsets = useSafeAreaInsets();
  const { isLargeUI } = useUISize();

  useEffect(() => {
    if (open) {
      sheet.current?.show();
    } else {
      sheet.current?.hide();
    }
  }, [open]);

  const maxWidth = useMaxWidth();
  const sheetWidth: ViewStyle['width'] = typeof maxWidth === 'number' ? maxWidth : '100%';
  const flattenedContainerStyle = StyleSheet.flatten(containerStyle ?? {});
  const resolvedContainerStyle: ViewStyle = isLargeUI
    ? { ...flattenedContainerStyle, width: sheetWidth }
    : flattenedContainerStyle;

  const paddingBottom = (() => {
    if (forceZeroPadding) {
      return 0;
    }
    const safeAreaPadding = useBottomSafeAreaPadding ? safeAreaInsets.bottom : 0;

    // This padding is added for aesthetic reasons on Android and web
    const supplementaryBottomPadding =
      Platform.OS === 'android'
        ? theme.spacing['spacing.general.x0,5']
        : !isMobileNative
          ? 26 + theme.spacing['spacing.general.x4']
          : 0;

    // Allow developers to add extra padding via containerStyle
    const extraPadding = (resolvedContainerStyle.paddingBottom as number | undefined) ?? 0;

    return safeAreaPadding + supplementaryBottomPadding + extraPadding;
  })();

  return (
    <ActionSheet
      {...rest}
      containerStyle={{ ...resolvedContainerStyle, paddingBottom }}
      initialSnapIndex={0}
      safeAreaInsets={{
        ...safeAreaInsets,
        bottom: 0,
        top: 90,
      }}
      // snapPoints={[maxSheetPercent]}
      gestureEnabled={rest.gestureEnabled ?? true}
      headerAlwaysVisible
      indicatorStyle={{
        backgroundColor: showIndicator ? theme.colors['text.primary'] : 'transparent',
        height: 5,
        width: 65,
        marginTop: showIndicator ? theme.spacing['spacing.general.x4'] : theme.spacing['spacing.general.x0,5'],
        marginBottom: theme.spacing['spacing.general.x4'],
        ...StyleSheet.flatten(indicatorStyle),
      }}
      isModal
      ref={sheet}
    >
      <View style={[{ flexShrink: 1 }, contentStyle]}>{rest.children}</View>
    </ActionSheet>
  );
}

BottomSheet.displayName = 'BottomSheet';

function normalizeActionContent(children: ReactNode): ReactNode {
  if (typeof children === 'string' || typeof children === 'number') {
    return <Text>{children}</Text>;
  }

  return Children.map(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return <Text>{child}</Text>;
    }

    if (!isValidElement(child) || child.type === Text) {
      return child;
    }

    const element = child as ReactElement<{ children?: ReactNode }>;
    if (!element.props.children) {
      return element;
    }

    return cloneElement(element, {
      children: normalizeActionContent(element.props.children),
    });
  });
}

BottomSheet.Action = function BottomSheetAction({
  children,
  onPress,
  prefix,
}: {
  children: ReactNode;
  onPress: () => void;
  prefix: ReactNode;
}) {
  const theme = useTheme<Theme>();
  const content = normalizeActionContent(children);

  return (
    <Button
      {...{ onPress }}
      pressableType={PressableType.GestureHandler}
      style={{
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        paddingVertical: theme.spacing['spacing.general.x4'],
        paddingHorizontal: theme.spacing['spacing.general.x2'],
        borderBottomWidth: 1,
        borderBottomColor: theme.colors['surface.disabled'],
        width: '100%',
      }}
      variant="plain"
    >
      <View style={{ alignItems: 'center', justifyContent: 'center', width: 32 }}>{prefix}</View>
      <View style={{ flex: 1, justifyContent: 'center', marginStart: theme.spacing['spacing.general.x2'] }}>
        {content}
      </View>
    </Button>
  );
};

BottomSheet.ActionButton = function BottomSheetActionButton({
  children,
  onPress,
  variant = 'primary',
  style,
}: {
  children: ReactNode;
  onPress: () => void;
  variant?: ButtonProps['variant'];
  style?: StyleProp<ViewStyle>;
}) {
  return (
    <Button pressableType={PressableType.GestureHandler} {...{ onPress, variant, style }}>
      {children}
    </Button>
  );
};
